import { useQueryClient } from "@tanstack/react-query"
import { LoversApi } from "api/LoversApi"
import { AxiosError } from "axios"
import { DatePicker } from "components/DatePicker"
import dayjs from "dayjs"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { formatTimeDiff } from "utils/FormatTimeDiff"

export const NewTimeCapsulePage = () => {
  const [name, setName] = useState("")
  const [opensAt, setOpensAt] = useState(dayjs().add(1, "day").toDate())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const res = await LoversApi().timeCapsule.createTimeCapsule({
        name,
        opensAt: opensAt.toISOString()
      })
      queryClient.invalidateQueries({ queryKey: ["useSelfTimeCapsules"] })
      navigate(`/time-capsules/${res.data.slug}`)
    } catch (err: unknown | AxiosError) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.errorMessage || "Failed to create time capsule")
      } else {
        setError("Failed to create time capsule")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <form onSubmit={handleSubmit}>
        <div className="space-y-12">
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base/7 font-semibold text-gray-900">
              Create new time capsule
            </h2>
            <p className="mt-1 text-sm/6 text-gray-600">
              Set up your time capsule with a name and date to be opened. You can always
              change these settings later.
            </p>

            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="name"
                  className="block text-sm/6 font-medium text-gray-900"
                >
                  Time capsule name
                </label>
                <div className="mt-2">
                  <div className="rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600 sm:max-w-md">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="For my 30th birthday"
                      className="block flex-1 border-0 bg-transparent p-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm/6 w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm/6 font-medium text-gray-900">
                  When to open
                </label>
                <div className="mt-2">
                  <div className="sm:max-w-md">
                    <DatePicker
                      defaultDate={opensAt}
                      onDateSelected={setOpensAt}
                      onlyFutureDates
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-x-6">
          {name && opensAt ? (
            <p className="block text-sm/6 font-medium text-gray-900">{`${name} will open in ${formatTimeDiff(opensAt.toISOString())}`}</p>
          ) : (
            <div className="hidden lg:block" />
          )}
          <div>
            {error && <p className="text-sm/6 text-red-500 text-right mb-3">{error}</p>}

            <button
              type="submit"
              disabled={isLoading || !name || !opensAt}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 w-full lg:w-auto disabled:opacity-50 disabled:hover:bg-primary-600"
            >
              {isLoading ? "Creating time capsule..." : "Create time capsule"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
